/**
 * Phase F-finale-7 Slice 7b.1 — IA self-scoped seat-assignment-config API client.
 *
 * Wraps the real-auth tenant-self BE surface added in Slice 7b.0
 * (eduviaos-backend PR #156):
 *
 *   GET    /api/seating/config
 *   PUT    /api/seating/config
 *   POST   /api/seating/config/reset
 *
 * Tenant + actor are resolved BE-side from the JWT `AuthContext`, and the
 * vertical is derived server-side from the tenant document — none of those
 * are client-trusted. These hit the bare `/api/seating/config` surface
 * (NOT the Phase-0 placeholder `PATCH /admin/capabilities/{key}` router,
 * which has no RBAC and trusts the client tenant header). Reads + writes
 * both need `MANAGE_ZONES` (enforced server-side via the seating
 * `_ADMIN_GUARD`; the FE additionally gates the UI via `useCanEditSeating`).
 *
 * The wire shapes mirror `app/domains/capability/payload_schemas/seat_assignment.py`
 * (`SeatAssignmentPayload`) + `app/domains/seating/schemas.py`
 * (`SeatAssignmentConfigResponse`) exactly — any field-shape change on the
 * BE must land here too. `fixed_assignment_method` + `fixed_assignment_order`
 * are only valid when `mode === "fixed"`; the BE 422s any other combination
 * and `buildWriteBody` nulls them out client-side so the UX never sends one.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

export type SeatAssignmentMode = "none" | "zone_only" | "dynamic" | "fixed" | "mixed";

/** Plan a `mixed` centre applies to a learner who has no plan of their own. */
export type MixedDefaultPlan = "first_come" | "flexible_hours" | "no_seat";

/** "Who assigns" axis — only meaningful when `mode === "fixed"`. */
export type FixedAssignmentMethod = "admin_assigns" | "student_picks" | "auto_round_robin";

/** "Ordering basis" axis — only meaningful when `mode === "fixed"`. */
export type FixedAssignmentOrder = "alphabetical" | "merit" | "manual";

export type SeatAssignmentSource = "tenant" | "vertical_default";

export type SeatAssignmentVertical = "school" | "coaching_institute" | "study_centre";

/** Canonical `SeatAssignmentPayload` fields (PUT body shape). */
export interface SeatAssignmentConfigBody {
  mode: SeatAssignmentMode;
  require_booking: boolean;
  allow_seat_switch: boolean;
  allow_session_extension: boolean;
  fixed_assignment_method: FixedAssignmentMethod | null;
  fixed_assignment_order: FixedAssignmentOrder | null;
  enforce_zone_capacity: boolean;
  waitlist_enabled: boolean;
  /** Whole-hour bookings beside the named shifts. Charge is hours times the rate. */
  custom_duration_enabled: boolean;
  /** Price of one hour, in paise. Must be above zero while custom hours are on. */
  custom_hourly_rate_paise: number;
  /** Only read in `mixed` mode. Always sent back on PUT so a save never resets it. */
  mixed_default_plan?: MixedDefaultPlan;
}

/** GET / PUT response — payload + provenance. */
export interface SeatAssignmentConfigResponse extends SeatAssignmentConfigBody {
  source: SeatAssignmentSource;
  vertical: SeatAssignmentVertical;
}

export type WriteSeatAssignmentConfigRequest = SeatAssignmentConfigBody;

// ── React Query hooks ─────────────────────────────────────────────────

const configKey = ["ia", "seating-config"] as const;

/** Cache-key for the resolved seat-assignment config. */
export const seatingConfigKeyPrefix = configKey;

export function useSeatConfig() {
  return useQuery<SeatAssignmentConfigResponse>({
    queryKey: configKey,
    queryFn: () => api.get<SeatAssignmentConfigResponse>("/api/seating/config"),
    staleTime: 15_000,
  });
}

export function useWriteSeatConfig() {
  const qc = useQueryClient();
  return useMutation<SeatAssignmentConfigResponse, ApiError, WriteSeatAssignmentConfigRequest>({
    mutationFn: (body) => api.put<SeatAssignmentConfigResponse>("/api/seating/config", body),
    onSuccess: (data) => {
      qc.setQueryData(configKey, data);
    },
  });
}

export function useResetSeatConfig() {
  const qc = useQueryClient();
  return useMutation<SeatAssignmentConfigResponse, ApiError, void>({
    mutationFn: () =>
      api.post<SeatAssignmentConfigResponse>("/api/seating/config/reset", undefined),
    onSuccess: (data) => {
      qc.setQueryData(configKey, data);
    },
  });
}

// ── Pure helpers (UI labels + write-body normalisation) ────────────────

export const SEAT_MODE_LABELS: Record<SeatAssignmentMode, string> = {
  none: "No seating",
  zone_only: "Zone only",
  dynamic: "Dynamic (per-session)",
  fixed: "Fixed (permanent)",
  mixed: "Mixed (per-learner plans)",
};

export const MIXED_DEFAULT_PLAN_LABELS: Record<MixedDefaultPlan, string> = {
  first_come: "First come, first served",
  flexible_hours: "Flexible hours",
  no_seat: "No seat — check in on arrival",
};

export const SEAT_MODE_HELP: Record<SeatAssignmentMode, string> = {
  none: "Learners simply check in — no zones, seats, or bookings are tracked.",
  zone_only: "Learners pick a zone (floor / room / section); individual seats are not tracked.",
  dynamic: "Seats are booked per session and freed afterwards — the everyday study-centre flow.",
  fixed: "Each learner is allocated a permanent seat that stays theirs across sessions.",
  mixed:
    "Each learner follows their own seat plan: a permanent seat, fixed hours, flexible hours, a room, or no seat.",
};

export const FIXED_METHOD_LABELS: Record<FixedAssignmentMethod, string> = {
  admin_assigns: "Admin assigns",
  student_picks: "Learner picks",
  auto_round_robin: "Auto (round-robin)",
};

export const FIXED_ORDER_LABELS: Record<FixedAssignmentOrder, string> = {
  alphabetical: "Alphabetical",
  merit: "By merit",
  manual: "Manual order",
};

/**
 * Normalise a draft into a valid PUT body. The BE rejects a non-null
 * `fixed_assignment_method` / `fixed_assignment_order` when `mode !==
 * "fixed"` (422), so we strip them client-side; conversely, entering
 * `fixed` mode defaults both axes so the body always satisfies the
 * cross-field invariant.
 */
export function buildWriteBody(draft: SeatAssignmentConfigBody): WriteSeatAssignmentConfigRequest {
  if (draft.mode === "fixed") {
    return {
      ...draft,
      fixed_assignment_method: draft.fixed_assignment_method ?? "admin_assigns",
      fixed_assignment_order: draft.fixed_assignment_order ?? "alphabetical",
    };
  }
  return {
    ...draft,
    fixed_assignment_method: null,
    fixed_assignment_order: null,
  };
}
